import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  Container,
  Group,
  ScrollArea,
  Button,
  Box,
  createStyles,
  Stack,
  SimpleGrid,
  Loader,
  Center,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import tinycolor from "tinycolor2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Member from "./Member";
import InvitePeople from "../settings/members/InvitePeople";
import SectionHeader from "../settings/layout/SectionHeader";
import FilterPanelController from "../presentations/filters/FilterPanelController";

import MemberModel from "../../../models/Member";
import SpaceMemberFilterModel from "../../../models/SpaceMemberFilters";

import { setTiptapSidemenu } from "../../../store/app";
import { useFilterPanel } from "../../hooks/useFilterPanel";

const paginator = {
  page: 1,
  per: 15,
};

const loader = {
  loading: false,
  oldScope: "all",
};

const useStyles = createStyles((theme, { colors }) => ({
  spaceContainer: {
    backgroundColor: colors.background_color,
    color: colors.default_text_color,
  },
  primaryButtonStyle: {
    backgroundColor: colors.primary_button_background_color,
    color: colors.primary_button_text_color,
    "&:hover": {
      backgroundColor: tinycolor(colors.primary_button_background_color)
        .darken(4)
        .toString(),
    },
  },
  secondaryButtonStyle: {
    backgroundColor: colors.secondary_button_background_color,
    color: colors.secondary_button_text_color,
    "&:hover": {
      backgroundColor: tinycolor(colors.secondary_button_background_color)
        .darken(4)
        .toString(),
    },
  },
  linkButtonStyle: {
    color: colors.header_link_color,
    "&:hover": {
      color: tinycolor(colors.header_link_color).darken(4).toString(),
    },
  },
  searchBar: {},
  title: {
    color: colors.default_text_color,
  },
  subtitle: {
    color: tinycolor(colors.default_text_color).darken(20).toString(),
  },
  memberItem: {
    color: colors.sidebar_text_color,
    backgroundColor: colors.sidebar_background_color,
    cursor: "pointer",
  },
  memberItemText: {
    color: colors.sidebar_text_color,
    "&:hover": {
      color: colors.sidebar_text_color,
    },
  },
  memberItemTextDimmed: {
    color: tinycolor(colors.sidebar_text_color).darken(5).toString(),
    "&:hover": {
      color: tinycolor(colors.sidebar_text_color).darken(5).toString(),
    },
  },
}));

export default function List(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const space = useSelector((state) => state.space);
  const tiptapSidemenu = useSelector((state) => state.tiptapSidemenu);
  const colors = useSelector((state) => state.colors);
  const { classes, cx, theme } = useStyles({ colors });
  const { term, setTerm, sortSelected, filtersSelected } = useFilterPanel();

  const is_beta = !!user.is_beta;

  const [opennedInvite, setOpenedInvite] = useState(false);
  const [members, setMembers] = useState({
    total: 0,
    page: 1,
    pages: 1,
    data: [],
  });
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [filtersVersion, setFiltersVersion] = useState(0);

  const viewport = useRef();
  const timeoutId = useRef();
  const nomore = useRef();
  const loadingCount = useRef(0);

  useEffect(() => {
    return () => {
      nomore.current = false;
    };
  }, []);

  useEffect(() => {
    if (space && space.slug) {
      onFetch();
      if (is_beta) {
        onFetchFilters();
      }
    }
  }, [space]);

  useEffect(() => {
    if (space && space.slug) {
      onFetch();
    }
  }, [term]);

  useEffect(() => {
    if (tiptapSidemenu && tiptapSidemenu.opened == "filter-members-close") {
      console.log("CLOSE PROFILE UPDATE");
      dispatch(setTiptapSidemenu({ opened: null }));
      onFetchFilters();
      onFetch();
    }
  }, [tiptapSidemenu]);

  const onFetch = () => {
    setLoading(true);
    if (loader.oldScope != scope) {
      paginator.page = 1;
      loader.oldScope = scope;
    }

    const filter_ids = Array.isArray(filtersSelected.current) && is_beta ? filtersSelected.current.map((item) => ({
      id: item.id,
      values: item.selectedOptions || [],
      is_term: ["title"].includes(item.format)
    })) : [];

    const sort = Array.isArray(sortSelected.current) && is_beta ? sortSelected.current.map((item) => ({
      id: item.id,
      value: item.value,
      sort_order: item.sort_order
    })) : [];

    MemberModel.onFetch(
      space.slug,
      paginator,
      { scope, term, filter_ids, sort },
      (data) => {
        loadingCount.current ++
        setLoading(false);
        if (paginator.page == 1) {
          setMembers({ ...data });
        } else {
          if (data.data.length > 0) {
            const auxMembers = { ...members };
            auxMembers.data = members.data.concat(data.data);
            setMembers(auxMembers);
          } else {
            paginator.page--;
            nomore.current = true;
          }
        }
      },
      (data) => {
        setLoading(false);
      }
    );
  };

  const onFetchFilters = () => {
    if (!window.$fetchedTopSlug) return;
    SpaceMemberFilterModel.onFetch(
      window.$fetchedTopSlug,
      (data) => {
        if (Array.isArray(data)) {
          setFilters(data);
        }
      },
      (error) => {
        console.log("ERROR FETCH SPACE FILTER:: ", error);
      }
    );
  };

  const onSelectFilter = (filter) => {
    filtersSelected.current = Array.isArray(filtersSelected.current) ? [...filtersSelected.current, filter] : [filter];
    // To update UI
    setFiltersVersion((v) => v + 1);
  };

  const onUpdateFilter = (filter) => {
    const updatedFilters = Array.isArray(filtersSelected.current) && filter ? filtersSelected.current.map((item) => {
      return item.id === filter.id ? filter : item;
    }) : [];
    filtersSelected.current = updatedFilters;
    paginator.page = 1;
    onFetch();
  };

  const onUpdateSort = (sort) => {
    sortSelected.current = sort ? sort : [];
    paginator.page = 1;
    onFetch();
    // To update UI
    setFiltersVersion((v) => v + 1);
  };

  const changePage = (page) => {
    paginator.page = page;
    onFetch();
  };

  const onScrollPositionChange = (pos) => {
    const div = viewport.current;

    if (pos.y + div.clientHeight >= div.scrollHeight - 100) {
      if (nomore.current) {
        return;
      }
      setLoading(true);
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      timeoutId.current = setTimeout(() => {
        changePage(paginator.page + 1);
      }, 1000);
    }
  };

  return (
    <Stack
      className={classes.spaceContainer}
      spacing={0}
      style={{ height: "100%" }}
    >
      <ScrollArea
        onScrollPositionChange={onScrollPositionChange}
        viewportRef={viewport}
        style={{ height: "100%" }}
        mt={10}
      >
        <Container
          size={space.space_width == "wide" ? 960 : 960}
          mt={20}
          mb={40}
        >
          <SectionHeader
            titleClass={classes.title}
            subtitleClass={classes.subtitle}
            spaceName={space.name}
            spaceId={space.slug}
            spaceVersion={space.version}
            title={t("spaces.members.sectionTitle")}
            rightSide={
              space && space.permissions && space.permissions.can_manage ? (
                <Group>
                  <Button
                    variant="light"
                    color="gray"
                    component={Link}
                    to={`/space/${space.slug}/settings/members`}
                    radius="xl"
                    className={classes.secondaryButtonStyle}
                  >
                    {t("spaces.members.manage")}
                  </Button>
                  <Button
                    variant="filled"
                    radius="xl"
                    onClick={() => setOpenedInvite(true)}
                    leftIcon={<FontAwesomeIcon icon={solid("user-plus")} />}
                    className={classes.primaryButtonStyle}
                  >
                    {t("spaces.members.invite")}
                  </Button>
                </Group>
              ) : null
            }
          />
          
          {is_beta && (
            <FilterPanelController
              type="filter-members"
              loading={loading}
              theme={theme}
              term={term}
              filters={filters}
              filtersSelected={filtersSelected.current}
              sortSelected={sortSelected.current}
              setTerm={setTerm}
              onSelectFilter={onSelectFilter}
              onUpdateFilter={onUpdateFilter}
              onUpdateSort={onUpdateSort}
            />
          )}

          {members.data.length == 0 && !loader.loading && loadingCount.current > 0 ? (
            <Box
              sx={(theme) => ({
                backgroundColor: colors.sidebar_background_color,
                textAlign: "center",
                padding: theme.spacing.xl,
                borderRadius: theme.radius.md,
                cursor: "pointer",
                marginTop: 20,
                color: colors.sidebar_text_color,
                "&:hover": {
                  backgroundColor: colors.sidebar_background_color,
                },
              })}
            >
              {t("spaces.members.emptyMessage")}
            </Box>
          ) : (
            <SimpleGrid
              cols={3}
              spacing={15}
              mt={20}
              breakpoints={[
                { maxWidth: 980, cols: 3, spacing: 15 },
                { maxWidth: 755, cols: 2, spacing: 15 },
                { maxWidth: 600, cols: 1, spacing: 15 },
              ]}
            >
              {members.data.map((member) => {
                const memberFilters = Array.isArray(member.member_filters) ? member.member_filters : [];
                const filteredMemberFilters = memberFilters.map((memberFilter) => {
                  const podMemberFilter = filters.find(f => f.id === memberFilter.pod_member_filter_id) || {};
                  return {
                    ...memberFilter,
                    sort_order: podMemberFilter.sort_order,
                    name: podMemberFilter.name,
                    options: podMemberFilter.options,
                    color: podMemberFilter.color,
                    format: podMemberFilter.format,
                    visibility: podMemberFilter.visibility,
                    emojicon: podMemberFilter.emojicon
                  };
                }).sort((a, b) => a.sort_order - b.sort_order);
                return (
                  <Member
                    memberItemClass={classes.memberItem}
                    memberItemTextClass={classes.memberItemText}
                    memberItemTextDimmed={classes.memberItemTextDimmed}
                    linkButtonStyle={classes.linkButtonStyle}
                    hasSubscriptions={
                      space && space.permissions
                        ? space.permissions.can_accept_charges
                        : false
                    }
                    spaceId={space.slug}
                    showEmail={
                      space && space.permissions
                        ? !space.permissions.hide_member_emails
                        : false
                    }
                    member={member}
                    memberFilters={is_beta ? filteredMemberFilters : []}
                  />
                );
              })}
            </SimpleGrid>
          )}
          {loading && (
            <Center mt={20}>
              <Loader />
            </Center>
          )}
          {opennedInvite && <InvitePeople opened={opennedInvite} setOpened={setOpenedInvite} />}
        </Container>
      </ScrollArea>
    </Stack>
  );
}
